/**
 * 搭配
 */
import React, { memo, useMemo } from 'react';
import { connect } from 'react-redux';
import cls from 'classnames';
import intl from 'react-intl-universal';

import DateJS, { format2FullTimeStr } from '@/utils/date';
import { getOccDuration, renderContent } from '@/utils';
import { OCC_TYPE_MAP, OccType } from '@/config/constant';
import ContentBlock from '@/components/Reservation/ContentBlock';
import IconFont from '@/components/IconFont';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import { swapRoomIndex } from '@/redux/actions/reservation/directRenewal';
import {
  ReducerState,
  storeName,
  RenewalRentRoomInfo,
} from '@/redux/reducers/reservation/directRenewal';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { rentRoomInfos, targetRoomMap } = state;
  return { rentRoomInfos, targetRoomMap };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callSwapRoom: swapRoomIndex,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface RoomGroupItem {
  roomTypeId: number;
  roomTypeName: string;
  roomList: RenewalRentRoomInfo[];
}

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const { rentRoomInfos, targetRoomMap, callSwapRoom } = props;

  const groupList = useMemo(() => {
    const result: RoomGroupItem[] = [];
    rentRoomInfos.forEach((item) => {
      const { roomTypeId, roomTypeName } = item;
      let groupItem = result.find((group) => group.roomTypeId === roomTypeId);
      if (!groupItem) {
        groupItem = { roomTypeId, roomTypeName, roomList: [] };
        result.push(groupItem);
      }
      groupItem.roomList.push(item);
    });
    return result;
  }, [rentRoomInfos]);

  // 预离时间
  const getETDTime = (origin: RenewalRentRoomInfo, target: RenewalRentRoomInfo) => {
    if (target.occType === OccType.TimeRENT) {
      // 钟点房预离时间
      return format2FullTimeStr(
        DateJS(origin.expectedDepartureTime).add(target.occDuration, 'hour'),
      );
    }
    return format2FullTimeStr(target.expectedDepartureTime);
  };

  // 房型列表
  const renderRoomTypeList = () => {
    return groupList.map((group) => {
      const { roomTypeId, roomTypeName, roomList } = group;
      const maxIndex = roomList.length - 1;
      return (
        <div key={roomTypeId}>
          <div className={styles.roomTypeName}>
            <span className={styles.originItemWrapper}>
              {intl.get('drawer_extension_room_type_prefix', { name: roomTypeName })}
            </span>
            <span className={styles.dividerItemWrapper} />
            <span className={styles.matchItemWrapper}>
              {intl.get('drawer_extension_room_type_prefix', { name: roomTypeName })}
            </span>
          </div>
          {roomList.map((room, idx) => {
            const targetRoom = targetRoomMap[room.targetId];
            return (
              <div className={styles.matchRoomItem} key={room.targetId}>
                <span className={styles.originItemWrapper}>
                  <span className={styles.originItemRoom}>
                    <div>{intl.get('app_constants_room_no')}</div>
                    <div>{room.roomNo}</div>
                  </span>
                  <span className={styles.originItemETD}>
                    <div>{intl.get('drawer_extension_time_old_etd_label')}</div>
                    <div>{format2FullTimeStr(room.expectedDepartureTime)}</div>
                  </span>
                </span>
                <span className={styles.dividerItemWrapper}>
                  <IconFont type="icon-duiyingjiantou1" />
                </span>
                <span className={styles.matchItemWrapper}>
                  <span className={cls(styles.matchItemCell, styles.shortItemCell)}>
                    <div>{intl.get('app_constants_room_no')}</div>
                    <div>{targetRoom.roomNo || intl.get('app_reservation_room_queue_no')}</div>
                  </span>
                  <span className={styles.matchItemCell}>
                    <div>{intl.get('app_reservation_channel_name')}</div>
                    <div>{targetRoom.sourceChannelName}</div>
                  </span>
                  <span className={styles.matchItemCell}>
                    <div>{intl.get('app_reservation_channel_no')}</div>
                    <div>{renderContent(targetRoom.channelOrderNo)}</div>
                  </span>
                  <span className={cls(styles.matchItemCell, styles.shortItemCell)}>
                    <div>{intl.get('drawer_extension_time_occ_type_label')}</div>
                    <div>{OCC_TYPE_MAP[targetRoom.occType]}</div>
                  </span>
                  <span className={cls(styles.matchItemCell, styles.shortItemCell)}>
                    <div>{intl.get('drawer_extension_time_occ_duration_label')}</div>
                    <div>
                      {getOccDuration({
                        type: targetRoom.occType,
                        duration: targetRoom.occDuration,
                        multi: false,
                      })}
                    </div>
                  </span>
                  <span className={styles.matchItemCell}>
                    <div>{intl.get('app_reservation_etd_time')}</div>
                    <div>{getETDTime(room, targetRoom)}</div>
                  </span>
                  <span
                    className={cls(styles.matchItemAction, {
                      [styles.matchItemActive]: idx > 0,
                    })}
                  >
                    <IconFont
                      type="icon-keshangyi"
                      onClick={() => callSwapRoom(room, roomList[idx - 1])}
                    />
                  </span>
                  <span
                    className={cls(styles.matchItemAction, {
                      [styles.matchItemActive]: idx < maxIndex,
                    })}
                  >
                    <IconFont
                      type="icon-kexiayi"
                      onClick={() => callSwapRoom(room, roomList[idx + 1])}
                    />
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      );
    });
  };
  return (
    <ContentBlock
      title={intl.get('drawer_extension_match_block_title')}
      className={styles.matchInfoBlock}
    >
      <div>
        <div className={styles.matchInfoHeader}>
          <span className={styles.originItemWrapper}>
            {intl.get('drawer_extension_match_origin_label')}
          </span>
          <span className={styles.dividerItemWrapper} />
          <span className={styles.matchItemWrapper}>
            {intl.get('drawer_extension_match_current_label')}
          </span>
        </div>
        <div>{renderRoomTypeList()}</div>
      </div>
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
